// SETTINGS V2 — Ceny a predvolby (Pricing & Defaults) page — SETTINGS_V2_SP7.
// Replaces the SP0 ComingSoonGhostV2 for /settings-v2/pricing. Reuses the
// serviceProviderPricing slice/API/DTO 100% UNCHANGED; ports the legacy
// SettingsPricing binding logic VERBATIM (seed / dirty / save coercions).
//
// The PUT is a WHOLE-ROW overwrite shared with SP8 (Služba), so it re-sends the
// passthrough keys it does not edit: the 3 BINDING radius/location keys (legacy
// already does this) AND — the SP7 fix — the 12 business-hours keys echoed from
// `settings` (§2 mitigation: legacy Pricing OMITS them, silently wiping the
// Service page's Business Hours on every save; DTO names verified matching).
// Known parity bug (§2): the period selects show `--` on load (frontend keys vs
// DTO `Default*` mismatch) — do NOT rename them; separate Servicing fix.
// No post-save refetch (PM ruling, §2): mount-refetch bounds the staleness.
import React, { useEffect, useMemo, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import ActionToast from "../../../../shared/components/ActionToast";
import {
  getPricingDefaults,
  savePricingDefaults,
  clearPricingSuccess,
  clearPricingError,
} from "../../serviceProviderPricingSlice";
import { SCard, SInputField, SSelectField, SSaveBtn, SComingPill } from "../atoms";
import PricingTermsCardV2 from "./PricingTermsCardV2";
import PricingIncludedCardV2 from "./PricingIncludedCardV2";
import type { IncludedKey } from "./PricingIncludedCardV2";

const cardAnim = (i: number): React.CSSProperties => ({
  animation: `sv2-card-reveal 0.4s ease-out ${(i * 0.06).toFixed(2)}s both`,
});

// The shared number-rate recipe (min 0, step 0.01 — legacy parity attributes).
const RateField: React.FC<{ label: string; value: string; onChange: (v: string) => void; unit: string }> = (p) => (
  <SInputField label={p.label} type="number" mono value={p.value} onChange={p.onChange} placeholder="0.00" unit={p.unit} inputProps={{ min: 0, step: 0.01 }} />
);

const PricingPageV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate: t } = useTranslation("settings");
  const { translate: tCommon } = useTranslation("common");
  const { isPhone } = useBreakpoint();

  // `loading` deliberately not consumed — parity (§2: no skeleton).
  const { settings, saving, success, error } = useAppSelector(
    (s) => s.serviceProviderPricing
  );

  // ── Local form state (mirrors legacy lines 70-89) ──
  const [hourlyRate, setHourlyRate] = useState("");
  const [travelRatePerKm, setTravelRatePerKm] = useState("");
  const [travelRatePerHour, setTravelRatePerHour] = useState("");
  const [warrantyPeriodCode, setWarrantyPeriodCode] = useState<number>(0);
  const [customWarranty, setCustomWarranty] = useState("");
  const [paymentTermsCode, setPaymentTermsCode] = useState<number>(0);
  const [customPaymentTerms, setCustomPaymentTerms] = useState("");
  const [validityPeriodCode, setValidityPeriodCode] = useState<number>(0);
  const [customValidity, setCustomValidity] = useState("");

  const [includeDiagnostics, setIncludeDiagnostics] = useState(false);
  const [includeLaborForRepair, setIncludeLaborForRepair] = useState(false);
  const [includePartsAndMaterials, setIncludePartsAndMaterials] = useState(false);
  const [includeTestingAndCalibration, setIncludeTestingAndCalibration] = useState(false);
  const [includeWorkAreaCleanup, setIncludeWorkAreaCleanup] = useState(false);
  const [includeDetailedServiceReport, setIncludeDetailedServiceReport] = useState(false);

  const [loadedValues, setLoadedValues] = useState<string>("");

  const includeSetters: Record<IncludedKey, React.Dispatch<React.SetStateAction<boolean>>> = {
    includeDiagnostics: setIncludeDiagnostics,
    includeLaborForRepair: setIncludeLaborForRepair,
    includePartsAndMaterials: setIncludePartsAndMaterials,
    includeTestingAndCalibration: setIncludeTestingAndCalibration,
    includeWorkAreaCleanup: setIncludeWorkAreaCleanup,
    includeDetailedServiceReport: setIncludeDetailedServiceReport,
  };
  const toggleInclude = (key: IncludedKey) => includeSetters[key]((v) => !v);

  // Mount: fetch settings (parity — bounds passthrough staleness across hops).
  useEffect(() => {
    dispatch(getPricingDefaults());
  }, [dispatch]);

  // Populate form when settings arrive (legacy lines 97-135 verbatim coercions).
  useEffect(() => {
    if (settings) {
      const hr = settings.hourlyRate != null ? String(settings.hourlyRate) : "";
      const trKm = settings.travelRatePerKm != null ? String(settings.travelRatePerKm) : "";
      const trHr = settings.travelRatePerHour != null ? String(settings.travelRatePerHour) : "";
      const wpc = settings.warrantyPeriodCode ?? 0;
      const cw = settings.customWarrantyPeriod ?? "";
      const ptc = settings.paymentTermsCode ?? 0;
      const cpt = settings.customPaymentTerms ?? "";
      const vpc = settings.validityPeriodCode ?? 0;
      const cv = settings.customValidityPeriod ?? "";
      const diag = settings.includeDiagnostics ?? false;
      const labor = settings.includeLaborForRepair ?? false;
      const parts = settings.includePartsAndMaterials ?? false;
      const testing = settings.includeTestingAndCalibration ?? false;
      const cleanup = settings.includeWorkAreaCleanup ?? false;
      const report = settings.includeDetailedServiceReport ?? false;

      setHourlyRate(hr); setTravelRatePerKm(trKm); setTravelRatePerHour(trHr);
      setWarrantyPeriodCode(wpc); setCustomWarranty(cw);
      setPaymentTermsCode(ptc); setCustomPaymentTerms(cpt);
      setValidityPeriodCode(vpc); setCustomValidity(cv);
      setIncludeDiagnostics(diag); setIncludeLaborForRepair(labor); setIncludePartsAndMaterials(parts);
      setIncludeTestingAndCalibration(testing); setIncludeWorkAreaCleanup(cleanup); setIncludeDetailedServiceReport(report);

      setLoadedValues(
        JSON.stringify({ hr, trKm, trHr, wpc, cw, ptc, cpt, vpc, cv, diag, labor, parts, testing, cleanup, report })
      );
    }
  }, [settings]);

  // Dirty detection (legacy lines 138-165 verbatim).
  const currentValues = useMemo(
    () =>
      JSON.stringify({
        hr: hourlyRate, trKm: travelRatePerKm, trHr: travelRatePerHour,
        wpc: warrantyPeriodCode, cw: customWarranty,
        ptc: paymentTermsCode, cpt: customPaymentTerms,
        vpc: validityPeriodCode, cv: customValidity,
        diag: includeDiagnostics, labor: includeLaborForRepair, parts: includePartsAndMaterials,
        testing: includeTestingAndCalibration, cleanup: includeWorkAreaCleanup, report: includeDetailedServiceReport,
      }),
    [
      hourlyRate, travelRatePerKm, travelRatePerHour,
      warrantyPeriodCode, customWarranty, paymentTermsCode, customPaymentTerms,
      validityPeriodCode, customValidity,
      includeDiagnostics, includeLaborForRepair, includePartsAndMaterials,
      includeTestingAndCalibration, includeWorkAreaCleanup, includeDetailedServiceReport,
    ]
  );
  const isDirty = loadedValues !== "" && currentValues !== loadedValues;

  const handleSave = () => {
    dispatch(
      savePricingDefaults({
        // ── 3 BINDING passthrough (legacy, keep — omit ⇒ Service radius/location WIPED) ──
        serviceRadiusKm: settings?.serviceRadiusKm ?? null,
        baseLatitude: settings?.baseLatitude ?? null,
        baseLongitude: settings?.baseLongitude ?? null,
        // ── 15 editable (legacy coercions verbatim) ──
        hourlyRate: hourlyRate ? Number(hourlyRate) : null,
        travelRatePerKm: travelRatePerKm ? Number(travelRatePerKm) : null,
        travelRatePerHour: travelRatePerHour ? Number(travelRatePerHour) : null,
        warrantyPeriodCode: warrantyPeriodCode || null, // parity key name — do NOT rename (§2)
        customWarrantyPeriod: customWarranty || null,
        paymentTermsCode: paymentTermsCode || null,
        customPaymentTerms: customPaymentTerms || null,
        validityPeriodCode: validityPeriodCode || null,
        customValidityPeriod: customValidity || null,
        includeDiagnostics,
        includeLaborForRepair,
        includePartsAndMaterials,
        includeTestingAndCalibration,
        includeWorkAreaCleanup,
        includeDetailedServiceReport,
        // ── 12 business-hours passthrough (TICKET-MANDATED MITIGATION §2) ──
        orderDeadlineEnabled: settings?.orderDeadlineEnabled ?? false,
        orderDeadlineTime: settings?.orderDeadlineTime ?? null,
        orderDeadlineTimezoneId: settings?.orderDeadlineTimezoneId ?? null,
        usePerDayDeadlines: settings?.usePerDayDeadlines ?? false,
        mondayDeadlineTime: settings?.mondayDeadlineTime ?? null,
        tuesdayDeadlineTime: settings?.tuesdayDeadlineTime ?? null,
        wednesdayDeadlineTime: settings?.wednesdayDeadlineTime ?? null,
        thursdayDeadlineTime: settings?.thursdayDeadlineTime ?? null,
        fridayDeadlineTime: settings?.fridayDeadlineTime ?? null,
        saturdayDeadlineTime: settings?.saturdayDeadlineTime ?? null,
        sundayDeadlineTime: settings?.sundayDeadlineTime ?? null,
      })
    ).then((result) => {
      if (savePricingDefaults.fulfilled.match(result)) {
        setLoadedValues(currentValues); // parity — no refetch (§2 ruling)
      }
    });
  };

  const twoCol: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr",
    gap: 12,
  };

  return (
    // Page root: NO animation (cards animate individually — a root transform
    // would trap the fixed-position ActionToast). Parity with ProfilePageV2.
    <div style={{ display: "flex", flexDirection: "column", gap: 11, minWidth: 0, flex: 1 }}>
      <ActionToast
        open={!!success}
        variant="success"
        message={t("settings:pricing.saveSuccess")}
        onClose={() => dispatch(clearPricingSuccess())}
        duration={6000}
      />
      <ActionToast
        open={!!error}
        variant="error"
        message={t("settings:pricing.saveError")}
        onClose={() => dispatch(clearPricingError())}
        duration={6000}
      />

      {/* Card 1 — Sadzby práce (the ONE Save persists all cards) */}
      <SCard
        title={t("settings:pricing.laborRates")}
        hint={t("settings:pricing.laborRatesDescription")}
        action={
          <SSaveBtn
            enabled={isDirty && !saving}
            busy={saving}
            label={saving ? t("settings:pricing.saving") : tCommon("buttons.save")}
            onClick={handleSave}
          />
        }
        style={cardAnim(0)}
      >
        <div style={twoCol}>
          <RateField label={t("settings:pricing.hourlyRate")} value={hourlyRate} onChange={setHourlyRate} unit={t("settings:pricing.unitPerHour")} />
          <SSelectField
            label={t("settings:pricing.currency")}
            value="EUR"
            onChange={() => {}}
            options={[{ value: "EUR", label: "EUR" }]}
            disabled
          />
        </div>
      </SCard>

      {/* Card 2 — Cestovné náklady */}
      <SCard
        title={t("settings:pricing.travelCosts")}
        hint={t("settings:pricing.travelCostsDescription")}
        style={cardAnim(1)}
      >
        <div style={twoCol}>
          <RateField label={t("settings:pricing.ratePerKm")} value={travelRatePerKm} onChange={setTravelRatePerKm} unit={t("settings:pricing.unitPerKm")} />
          <RateField label={t("settings:pricing.ratePerHourTravel")} value={travelRatePerHour} onChange={setTravelRatePerHour} unit={t("settings:pricing.unitPerHour")} />
        </div>
      </SCard>

      {/* Card 3 — Záruka a podmienky */}
      <PricingTermsCardV2
        warrantyPeriodCode={warrantyPeriodCode}
        paymentTermsCode={paymentTermsCode}
        validityPeriodCode={validityPeriodCode}
        customWarranty={customWarranty}
        customPaymentTerms={customPaymentTerms}
        customValidity={customValidity}
        onWarrantyChange={setWarrantyPeriodCode}
        onPaymentChange={setPaymentTermsCode}
        onValidityChange={setValidityPeriodCode}
        onCustomWarrantyChange={setCustomWarranty}
        onCustomPaymentChange={setCustomPaymentTerms}
        onCustomValidityChange={setCustomValidity}
        isPhone={isPhone}
        animationDelay="0.12s"
      />

      {/* Card 4 — Zahrnuté služby */}
      <PricingIncludedCardV2
        values={{
          includeDiagnostics,
          includeLaborForRepair,
          includePartsAndMaterials,
          includeTestingAndCalibration,
          includeWorkAreaCleanup,
          includeDetailedServiceReport,
        }}
        onToggle={toggleInclude}
        isPhone={isPhone}
        animationDelay="0.18s"
      />

      {/* Card 5 — Fakturácia (future) */}
      <SCard
        title={t("settings:pricing.invoicing")}
        action={<SComingPill label={t("settings:pricing.comingSoon")} />}
        style={cardAnim(4)}
      >
        <div style={{ fontSize: 12.5, color: "var(--faint)", lineHeight: 1.55 }}>
          {t("settings:pricing.invoicingHint")}
        </div>
      </SCard>
    </div>
  );
};

export default PricingPageV2;
